"use client";

import { useRef, useState } from "react";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Users,
} from "lucide-react";

import type { TourDto } from "@tripcart/shared";
import { DEFAULT_CURRENCY_FORMAT, formatMoney } from "@tripcart/shared";

import { cx } from "../../cx";
import styles from "./related-tours.module.css";

/** The subset of a tour one card renders. Kept narrow so callers serialize
 *  only these fields into the RSC payload. */
export type RelatedTourCard = {
  id: TourDto["id"];
  name: TourDto["name"];
  durationLabel: TourDto["durationLabel"];
  publicUrlPath: TourDto["publicUrlPath"];
  heroImage: Pick<TourDto["heroImage"], "url" | "alt">;
  destination: Pick<TourDto["destination"], "name">;
  /** Absent when the tour caps nothing; the card then omits its group line. */
  groupSize?: Pick<NonNullable<TourDto["groupSize"]>, "max"> | undefined;
  priceFrom: TourDto["priceFrom"];
  /** The list price, struck through beside "Starting from" when higher. */
  priceFromWas?: TourDto["priceFromWas"];
  /**
   * The operator's marketing ribbon — the builder's Advanced → Miscellaneous
   * badge ("Best seller", "Small group"). Drives the pill on the image.
   *
   * This pill showed `activity` (the tour's activity type) until 2026-10-26.
   * An activity type is catalogue taxonomy; this pill is merchandising, and
   * the operator asked for their own ribbon in it. The badge is per-tour and
   * is their claim about THIS tour, which is what a ribbon is for.
   *
   * `badge` is OPTIONAL, so a tour with none renders no pill where `activity`
   * always drew something. Intended: a card with nothing to boast about says
   * nothing.
   *
   * This comment previously said a related list is built from tours sharing
   * one activity type, so the pill repeated a single word. Not true — the
   * caller builds it from `?destination=<slug>`.
   */
  badge?: TourDto["badge"];
};

/** Every string the section renders, resolved by the caller (this package
 *  has no translator). `maxGroup` carries a `{max}` token and `amountOff` an
 *  `{amount}` token, substituted here. */
export interface RelatedToursLabels {
  title: string;
  description: string;
  viewAll: string;
  startingFrom: string;
  perPerson: string;
  pricingSoon: string;
  maxGroup: string;
  amountOff: string;
  previous: string;
  next: string;
}

const DEFAULT_RELATED_LABELS: RelatedToursLabels = {
  title: "Similar tours you might like",
  description: "More tours from this operator.",
  viewAll: "View all tours",
  startingFrom: "Starting from",
  perPerson: "per person",
  pricingSoon: "Pricing coming soon",
  maxGroup: "Max {max}",
  amountOff: "{amount} off",
  previous: "Previous tours",
  next: "More tours",
};

const money = (amount: number, currencyCode: string) =>
  formatMoney(amount, { ...DEFAULT_CURRENCY_FORMAT, currencyCode });

/** The struck-through list price, or null. The currencies must match and the
 *  was-price must be genuinely higher, or there is no saving to show. */
function wasLabel(t: RelatedTourCard): string | null {
  const was = t.priceFromWas;
  if (!was || !t.priceFrom) return null;
  if (was.currencyCode !== t.priceFrom.currencyCode) return null;
  if (was.amount <= t.priceFrom.amount) return null;
  return money(was.amount, was.currencyCode);
}

/** The saving for the discount pill; null exactly when `wasLabel` is. */
function savingLabel(t: RelatedTourCard): string | null {
  const was = t.priceFromWas;
  if (!wasLabel(t) || !was || !t.priceFrom) return null;
  return money(was.amount - t.priceFrom.amount, was.currencyCode);
}

/** Scrolls the rail by one viewport; scroll-snap lands it on a card edge. */
function RailButton({
  rail,
  direction,
  label,
}: {
  rail: React.RefObject<HTMLDivElement | null>;
  direction: "back" | "forward";
  label: string;
}) {
  const Icon = direction === "back" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      data-tui=""
      aria-label={label}
      onClick={() => {
        const el = rail.current;
        if (!el) return;
        el.scrollBy({
          left: direction === "back" ? -el.clientWidth : el.clientWidth,
          behavior: "smooth",
        });
      }}
      className={styles.railButton}
    >
      <Icon strokeWidth={2.2} />
    </button>
  );
}

function CardPrice({ t, l }: { t: RelatedTourCard; l: RelatedToursLabels }) {
  const was = wasLabel(t);
  if (!t.priceFrom) return <div className={styles.meta}>{l.pricingSoon}</div>;
  return (
    <>
      <div className={styles.from}>
        {l.startingFrom}
        {was ? <s className={styles.was}>{was}</s> : null}
      </div>
      <div className={styles.price}>
        {money(t.priceFrom.amount, t.priceFrom.currencyCode)}
        <span className={styles.perPerson}>{l.perPerson}</span>
      </div>
    </>
  );
}

/** Location, the two-line title, and the duration / group-size facts. */
function CardFacts({ t, l }: { t: RelatedTourCard; l: RelatedToursLabels }) {
  return (
    <>
      {t.destination.name?.trim() ? (
        <div className={styles.location}>
          <MapPin strokeWidth={2} />
          <span className={styles.truncate}>{t.destination.name}</span>
        </div>
      ) : null}
      <div className={styles.name}>{t.name}</div>
      <div className={styles.facts}>
        {t.durationLabel ? (
          <span className={styles.fact}>
            <Clock strokeWidth={2} />
            {t.durationLabel}
          </span>
        ) : null}
        {t.groupSize ? (
          <span className={styles.fact}>
            <Users strokeWidth={2} />
            {l.maxGroup.replace("{max}", String(t.groupSize.max))}
          </span>
        ) : null}
      </div>
    </>
  );
}

/** One card; the whole card is the link. */
function SimilarTourCard({
  t,
  l,
}: {
  t: RelatedTourCard;
  l: RelatedToursLabels;
}) {
  const off = savingLabel(t);
  const [broken, setBroken] = useState(false);
  return (
    <a href={t.publicUrlPath} data-tui="" className={styles.card}>
      <div className={styles.media}>
        <div className={styles.frame}>
          {t.heroImage.url && !broken ? (
            <img
              src={t.heroImage.url}
              className={styles.image}
              alt={t.heroImage.alt}
              loading="lazy"
              decoding="async"
              onError={() => setBroken(true)}
            />
          ) : null}
        </div>
        <div className={styles.badges}>
          {t.badge ? (
            <span className={cx(styles.pill, styles.category)}>
              <span className={styles.pillText}>{t.badge}</span>
            </span>
          ) : null}
          {off ? (
            <span className={cx(styles.pill, styles.discount)}>
              <span className={styles.pillText}>
                {l.amountOff.replace("{amount}", off)}
              </span>
            </span>
          ) : null}
        </div>
      </div>

      <div className={styles.body}>
        <CardFacts t={t} l={l} />
        <div className={styles.foot}>
          <div className={styles.priceBlock}>
            <CardPrice t={t} l={l} />
          </div>
          <span aria-hidden className={styles.go}>
            <ArrowRight strokeWidth={2} />
          </span>
        </div>
      </div>
    </a>
  );
}

/** The "Similar tours you might like" rail: a snapping row of cards whose
 *  half-visible last card tells the reader there is more. */
export function RelatedToursSection({
  tours,
  labels,
}: {
  tours: RelatedTourCard[];
  labels?: RelatedToursLabels;
}) {
  const railRef = useRef<HTMLDivElement>(null);

  if (!tours.length) return null;

  const l = labels ?? DEFAULT_RELATED_LABELS;

  return (
    <section data-slot="related-tours" className={styles.section}>
      <div className={styles.headRow}>
        <div className={styles.heading}>
          <h2 className={styles.title}>{l.title}</h2>
          <p className={styles.description}>{l.description}</p>
        </div>
        <div className={styles.nav}>
          <a href="/tours" data-tui="" className={styles.viewAll}>
            {l.viewAll}
          </a>
          {/* Desktop only: on a phone the swipe is the gesture. */}
          <span className={styles.arrows}>
            <RailButton rail={railRef} direction="back" label={l.previous} />
            <RailButton rail={railRef} direction="forward" label={l.next} />
          </span>
        </div>
      </div>

      <div ref={railRef} className={styles.rail}>
        {tours.map((t) => (
          <SimilarTourCard key={t.id} t={t} l={l} />
        ))}
      </div>
    </section>
  );
}
